import { FiChevronDown, FiSearch } from 'react-icons/fi';

export default function LeadTopControls({
  canExportReports,
  onOpenExport,
  searchInput,
  setSearchInput,
  selectedCategory,
  setSelectedCategory,
  visaTypeFilters,
  selectedStatus,
  setSelectedStatus,
  statusFilters,
  selectedVisaExpiryDays,
  setSelectedVisaExpiryDays,
  selectedProcessType,
  setSelectedProcessType,
  processTypes,
  agentDropdownRef,
  isAgentDropdownOpen,
  setIsAgentDropdownOpen,
  selectedAgents,
  activeAgents,
  handleAgentToggle,
  clearAgentSelection,
  customDateSelector,
}) {
  const visaTypeOptions = (() => {
    const titles = (visaTypeFilters || [])
      .map((category) => category?.title)
      .filter(Boolean);

    if (!titles.includes('All')) {
      titles.unshift('All');
    }

    if (selectedCategory && !titles.includes(selectedCategory)) {
      titles.unshift(selectedCategory);
    }

    return [...new Set(titles)];
  })();

  return (
    <>
      <div className="mb-4 w-full flex justify-end">
        <button
          type="button"
          onClick={() => {
            if (!canExportReports) {
              alert('Only super-admin or sub-admin can generate reports.');
              return;
            }
            onOpenExport();
          }}
          className="w-[160px] bg-[#87CA60] text-black py-2 px-4 rounded-full hover:bg-[#72b34d] focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Export
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-base font-normal md:text-xl lg:text-2xl lg:font-medium">
          All Applications
        </h1>
        <div className="flex items-center w-full sm:w-[450px] justify-end">
          <div className="relative w-full max-w-xl">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or ID"
              className="w-full rounded-full border border-gray-200 py-2.5 pl-10 pr-4 focus:border-blue-500 focus:outline-none"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="w-full mb-6">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:flex xl:flex-nowrap xl:flex-1 xl:items-end xl:gap-2">
            <div className="relative w-full xl:w-[150px] xl:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                title={selectedCategory}
              >
                {visaTypeOptions.map((categoryTitle) => (
                  <option key={categoryTitle} value={categoryTitle}>
                    {categoryTitle}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            <div className="relative w-full xl:w-[155px] xl:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                title={selectedStatus}
              >
                {statusFilters.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            <div className="relative w-full xl:w-[130px] xl:flex-none">
              <input
                type="number"
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#005787] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                value={selectedVisaExpiryDays}
                onChange={(e) => setSelectedVisaExpiryDays(e.target.value)}
                placeholder="Visa expiry"
                min={1}
                max={365}
              />
            </div>

            <div className="relative w-full xl:w-[140px] xl:flex-none">
              <select
                className="w-full max-w-full appearance-none truncate rounded border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
                value={selectedProcessType}
                onChange={(e) => setSelectedProcessType(e.target.value)}
                title={selectedProcessType}
              >
                <option value="">Process Type</option>
                {processTypes.map((pt) => (
                  <option key={pt._id} value={pt._id} className="whitespace-normal break-words">
                    {pt.name}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500" />
            </div>

            <div ref={agentDropdownRef} className="relative w-full xl:w-[140px] xl:flex-none">
              <button
                type="button"
                onClick={() => setIsAgentDropdownOpen((prev) => !prev)}
                className="flex w-full items-center justify-between rounded border border-gray-300 bg-white py-2 pl-3 pr-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#005787]"
              >
                <span className="truncate">
                  {selectedAgents.length > 0 ? `${selectedAgents.length} selected` : 'Agents'}
                </span>
                <FiChevronDown
                  className={`ml-1 shrink-0 text-gray-500 transition-transform ${
                    isAgentDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isAgentDropdownOpen && (
                <div className="absolute left-0 top-full z-30 mt-1 w-48 rounded border border-gray-200 bg-white shadow-lg">
                  <div className="max-h-56 overflow-y-auto p-1">
                    {activeAgents.length === 0 ? (
                      <p className="px-3 py-2 text-sm text-gray-400">No agents found</p>
                    ) : (
                      activeAgents.map((agent) => (
                        <label
                          key={agent._id}
                          className="flex cursor-pointer items-center gap-2 rounded px-3 py-1.5 text-sm hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[#005787]"
                            checked={selectedAgents.includes(agent._id)}
                            onChange={() => handleAgentToggle(agent._id)}
                          />
                          <span className="truncate">
                            {agent.firstName} {agent.lastName}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                  {selectedAgents.length > 0 && (
                    <div className="border-t border-gray-100 px-3 py-1.5">
                      <button
                        onClick={clearAgentSelection}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Clear selection
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end xl:justify-start xl:flex-none xl:min-w-[260px]">
            <div className="w-full md:max-w-[400px] xl:max-w-none xl:w-auto">
              {customDateSelector}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
